import { useEffect } from 'react';
import { isDemo } from '@/lib/env';
import { Link, useLocation } from 'react-router';
import { LayoutDashboard, Shield, Wrench, UserCircle, Sparkles } from 'lucide-react';

const BANNER_HEIGHT = 44;

const navItems = [
  { to: '/client-dashboard', icon: LayoutDashboard, label: 'Client View', short: 'Client' },
  { to: '/helper-dashboard', icon: Wrench, label: 'Helper View', short: 'Helper' },
  { to: '/admin', icon: Shield, label: 'Admin View', short: 'Admin' },
  { to: '/client-account', icon: UserCircle, label: 'Account', short: 'Acct' },
];

export function InvestorDemoBanner() {
  const location = useLocation();

  useEffect(() => {
    if (!isDemo) return;
    document.body.style.paddingTop = `${BANNER_HEIGHT}px`;
    document.documentElement.style.setProperty('--staging-banner-height', `${BANNER_HEIGHT}px`);
    return () => {
      document.body.style.paddingTop = '';
      document.documentElement.style.removeProperty('--staging-banner-height');
    };
  }, []);

  if (!isDemo) return null;

  return (
    <div
      role="banner"
      aria-label="Investor demo environment"
      className="fixed top-0 inset-x-0 z-[9999] bg-primary text-primary-foreground shadow-md"
      style={{ height: 44 }}
    >
      <div className="max-w-7xl mx-auto h-full px-3 flex items-center justify-between gap-2">
        {/* Left: label */}
        <div className="flex items-center gap-1.5 text-xs sm:text-sm font-bold tracking-wide shrink-0">
          <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
          <span>INVESTOR DEMO</span>
          {/* Full opacity: at opacity-80 this sat at 3.56:1 on the clay banner,
              the last colour-contrast violation axe reported across all ten
              public routes. The label beside it is already bold, so the weight
              difference alone carries the hierarchy. */}
          <span className="hidden md:inline font-normal">— No real data. Auth bypassed.</span>
        </div>

        {/* Right: quick-nav — always shows labels (short on mobile, full on sm+) */}
        <nav className="flex items-center gap-0.5 sm:gap-2 text-xs font-medium shrink-0">
          {navItems.map(({ to, icon: Icon, label, short }) => {
            const isActive =
              location.pathname === to || location.pathname.startsWith(to + '/');
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1 rounded px-1.5 sm:px-2 py-1 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-card text-primary font-bold'
                    : 'bg-primary/80/40 hover:bg-primary/80/60'
                }`}
              >
                <Icon className="h-3 w-3 shrink-0" />
                <span className="sm:hidden text-[10px]">{short}</span>
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
